import { useState } from "react";
import clsx from "clsx";
import { format } from "date-fns";
import { config } from "@chef/constants";

import { Rating } from "../Rating";
import { Card } from "../Card";

const intl = {
  no: {
    READ_MORE: "Les mer",
    READ_LESS: "Les mindre",
    SCROLL_LEFT: "Scroll til venstre",
    SCROLL_RIGHT: "Scroll til høyre",
  },
  se: {
    READ_MORE: "Läs mer",
    READ_LESS: "Läs mindre",
    SCROLL_LEFT: "Scrolla vänster",
    SCROLL_RIGHT: "Scrolla höger",
  },
  dk: {
    READ_MORE: "Læs mere",
    READ_LESS: "Læse mindre",
    SCROLL_LEFT: "Scroll til venstre",
    SCROLL_RIGHT: "Scroll til højre",
  },
};

interface ReviewCardProps {
  review: {
    id: string;
    text: string;
    title: string;
    stars: number;
    createdAt: string | null;
    consumer: {
      displayName: string | null;
    };
    location: string | null;
  };
  language?: keyof typeof intl;
}

export const ReviewCard = ({ review, language = "no" }: ReviewCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const canExpand = review.text.length > 140 || review.title.length > 40;
  const { READ_MORE, READ_LESS } = intl[language];
  return (
    <article className="inline-flex justify-center w-full shrink snap-center">
      <Card
        noPadding
        className={
          "mb-5 p-10 space-y-6 w-80 lg:w-112 min-h-[24rem] flex flex-col rounded-sm"
        }
      >
        <Rating value={review.stars} numOptions={5} readOnly />
        <strong className={clsx(!expanded && "line-clamp-2", "text-xl")}>
          {review.title}
        </strong>
        <div className="grow">
          <p className={clsx(!expanded && "line-clamp-3")} title={review.text}>
            {review.text}
          </p>
          {canExpand && (
            <aside>
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-sm underline"
              >
                {!expanded ? READ_MORE : READ_LESS}
              </button>
            </aside>
          )}
        </div>
        <div className="flex flex-col">
          <strong>{review.consumer.displayName}</strong>
          {review.location && (
            <span className="h-4 text-grey-1">{review.location}</span>
          )}

          {review.createdAt && (
            <span className="h-4 text-grey-1">
              {format(new Date(review.createdAt), "dd. MMMM yy", {
                locale: config.locale,
              })}
            </span>
          )}
        </div>
      </Card>
    </article>
  );
};
